// FieldVision - ZoneMapView.tsx
// Leaflet kort med zone-polygoner farvet efter zone nummer
// FIX Issue 3: Kan nu vise field outline (groen polygon) naar ingen tildelingskort
// FIX: Tooltip viser nu korrekt areal via zones prop lookup
// UPDATE 2025-12-31: Nye zone farver, fuld opacity, hover med moerkere farver
// NEW 2025-01-10: Support for overlay geometry (application log GPS polygon)

import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { ZoneData, ZONE_COLORS_HOVER } from './zones/ZoneRow';

import SprayWeatherWidget from './SprayWeatherWidget';
interface ZoneMapViewProps {
  geoJson: any | null;
  zones: ZoneData[];
  zoneColors: Record<number, string>;
  emptyState?: boolean;
  overlayGeometry?: any | null;
}

const DENMARK_CENTER: [number, number] = [56.0, 10.0];
const DEFAULT_ZOOM = 15;

const calculateBounds = (geoJson: any): [[number, number], [number, number]] | null => {
  if (!(geoJson as any)?.features || (geoJson as any).features.length === 0) {
    return null;
  }

  let minLat = Infinity, maxLat = -Infinity;
  let minLon = Infinity, maxLon = -Infinity;

  for (const feature of (geoJson as any).features) {
    if (!(feature as any).geometry?.coordinates) continue;

    const coordArrays = (feature as any).geometry.type === 'MultiPolygon'
      ? (feature as any).geometry.coordinates.flat(2)
      : (feature as any).geometry.coordinates.flat(1);

    for (const coord of coordArrays) {
      if (Array.isArray(coord) && coord.length >= 2) {
        const lon = coord[0];
        const lat = coord[1];
        if (typeof lat === 'number' && typeof lon === 'number') {
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
          minLon = Math.min(minLon, lon);
          maxLon = Math.max(maxLon, lon);
        }
      }
    }
  }

  if (minLat === Infinity) return null;

  return [[minLat, minLon], [maxLat, maxLon]];
};

const MapBoundsUpdater: React.FC<{ geoJson: any }> = ({ geoJson }) => {
  const map = useMap();
  const isMounted = React.useRef(true);

  useEffect(() => {
    return () => { 
      isMounted.current = false;
      try {
        if (map.getContainer()) {
          map.stop();
        }
      } catch (e) {
        // Map already destroyed - ignore
      }
    };
  }, [map]);

  useEffect(() => {
    if (!isMounted.current) return;
    if (geoJson && (geoJson as any).features && (geoJson as any).features.length > 0) {
      try {
        const L = (window as any).L;
        const bounds = new L.geoJSON(geoJson).getBounds();
        if (bounds.isValid() && isMounted.current && map.getContainer()) {
          map.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 17,
            animate: false  // Undgår race condition ved hurtig navigation
          });
        }
      } catch (e) {
        console.error('Error fitting bounds:', e);
      }
    }
  }, [geoJson, map]);

  return null;
};

const ZoneMapView: React.FC<ZoneMapViewProps> = ({
  geoJson,
  zones,
  zoneColors,
  emptyState = false,
  overlayGeometry = null
}) => {
  // FIX: Opret lookup for baade rates og hectares fra zones prop
  const zoneLookup = useMemo(() => {
    const rates: Record<number, number> = {};
    const hectares: Record<number, number> = {};
    zones.forEach((z: any) => {
      rates[(z as any).zone] = (z as any).rate;
      hectares[(z as any).zone] = (z as any).hectares || 0;
    });
    return { rates, hectares };
  }, [zones]);

  const getFeatureStyle = (feature: any) => {
    // FIX Issue 3: Hvis det er emptyState, brug groen farve
    if (emptyState || (feature as any)?.properties?.isOutline) {
      return {
        fillColor: '#22c55e',
        fillOpacity: 0.2,
        color: '#16a34a',
        weight: 3,
        opacity: 1
      };
    }

    const zoneNumber = (feature as any)?.properties?.zone || 0;
    const color = zoneColors[zoneNumber] || '#9ca3af';

    return {
      fillColor: color,
      fillOpacity: 1,  // UPDATE: Fuld opacity i stedet for 0.6
      color: color,
      weight: 2,
      opacity: 1
    };
  };

  const onEachFeature = (feature: any, layer: any) => {
    // FIX Issue 3: Anderledes tooltip for field outline
    if (emptyState || (feature as any)?.properties?.isOutline) {
      const marknr = (feature as any)?.properties?.marknr || 'Mark';
      const areal = (feature as any)?.properties?.areal || 0;

      const tooltipContent = `
        <div style="padding: 4px 8px;">
          <strong>${marknr}</strong><br/>
          ${(areal as number).toFixed(1)} ha<br/>
          <em style="color: #6b7280;">Ingen tildelingskort</em>
        </div>
      `;

      layer.bindTooltip(tooltipContent, {
        permanent: false,
        sticky: true,
        direction: 'top',
        className: 'zone-tooltip'
      });
    } else {
      const zoneNumber = (feature as any)?.properties?.zone || 0;
      // FIX: Hent hectares fra zones lookup i stedet for feature properties
      const hectares = zoneLookup.hectares[zoneNumber] || (feature as any)?.properties?.hectares || 0;
      const rate = zoneLookup.rates[zoneNumber] || (feature as any)?.properties?.rate || 0;
      const unit = (feature as any)?.properties?.unit || 'l/ha';

      const tooltipContent = `
        <div style="padding: 4px 8px;">
          <strong>Zone ${zoneNumber}</strong><br/>
          ${rate} ${unit}<br/>
          ${(hectares as number).toFixed(2)} ha
        </div>
      `;

      layer.bindTooltip(tooltipContent, {
        permanent: false,
        sticky: true,
        direction: 'top',
        className: 'zone-tooltip'
      });
    }

    // Gem original farve for at kunne gendanne den
    const zoneNumber = (feature as any)?.properties?.zone || 0;
    const originalColor = zoneColors[zoneNumber] || '#9ca3af';
    const hoverColor = ZONE_COLORS_HOVER[zoneNumber] || originalColor;

    layer.on({
      mouseover: (e: any) => {
        if (emptyState || (feature as any)?.properties?.isOutline) {
          e.target.setStyle({
            fillOpacity: 0.4,
            weight: 4
          });
        } else {
          // UPDATE: Brug moerkere hover-farve i stedet for opacity
          e.target.setStyle({
            fillColor: hoverColor,
            color: hoverColor,
            weight: 3
          });
        }
      },
      mouseout: (e: any) => {
        if (emptyState || (feature as any)?.properties?.isOutline) {
          e.target.setStyle({
            fillOpacity: 0.2,
            weight: 3
          });
        } else {
          // UPDATE: Gendab original farve
          e.target.setStyle({
            fillColor: originalColor,
            color: originalColor,
            weight: 2
          });
        }
      }
    });
  };

  const initialCenter = useMemo(() => {
    if ((geoJson as any)?.features?.length > 0) {
      const bounds = calculateBounds(geoJson);
      if (bounds) {
        return [
          (bounds[0][0] + bounds[1][0]) / 2,
          (bounds[0][1] + bounds[1][1]) / 2
        ] as [number, number];
      }
    }
    return DENMARK_CENTER;
  }, [geoJson]);

  return (
    <div className="zone-map-view" style={{ position: 'relative' }}>
      <MapContainer
        center={initialCenter}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%' }}
        className="zone-leaflet-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {geoJson && (geoJson as any).features && (geoJson as any).features.length > 0 && (
          <>
            <GeoJSON
              data={geoJson}
              style={getFeatureStyle}
              onEachFeature={onEachFeature}
              key={`geojson-${emptyState ? 'outline' : JSON.stringify(zones.map((z: any) => (z as any).rate))}`}
            />
            <MapBoundsUpdater geoJson={geoJson} />
          </>
        )}

        {overlayGeometry && (
          <GeoJSON
            key="application-log-overlay"
            data={overlayGeometry}
            style={{
              color: '#3b82f6',
              weight: 3,
              opacity: 0.9,
              fillColor: '#3b82f6',
              fillOpacity: 0.1,
              dashArray: '10, 5',
            }}
          />
        )}
      </MapContainer>
        <SprayWeatherWidget />

      {(!geoJson || !(geoJson as any).features || (geoJson as any).features.length === 0) && (
        <div className="map-placeholder">
          <div className="placeholder-content">
            <svg viewBox="0 0 100 100" className="placeholder-icon">
              <polygon
                points="50,10 85,30 85,70 50,90 15,70 15,30"
                fill="#e5e7eb"
                stroke="#d1d5db"
                strokeWidth="2"
              />
              <text x="50" y="55" textAnchor="middle" fill="#9ca3af" fontSize="12">?</text>
            </svg>
            <p>Vaelg et kort for at se zone-polygoner</p>
          </div>
        </div>
      )}

      {/* FIX Issue 3: Info boks naar vi viser field outline */}
      {emptyState && geoJson && (
        <div className="outline-info">
          <span className="outline-icon">📍</span>
          Mark uden tildelingskort
        </div>
      )}

      <style>{`
        .zone-map-view {
          height: 100%;
          width: 100%;
          position: relative;
        }

        .zone-leaflet-map {
          height: 100%;
          width: 100%;
          z-index: 1;
        }

        .map-placeholder {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          z-index: 2;
        }

        .placeholder-content {
          text-align: center;
          color: #9ca3af;
        }

        .placeholder-icon {
          width: 80px;
          height: 80px;
          margin-bottom: 16px;
        }

        .placeholder-content p {
          margin: 0;
          font-size: 14px;
        }

        /* FIX Issue 3: Info boks for field outline */
        .outline-info {
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 8px 16px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          font-size: 13px;
          color: #374151;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .outline-icon {
          font-size: 16px;
        }

        .zone-tooltip {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          padding: 0;
          font-size: 13px;
        }

        .zone-tooltip::before {
          border-top-color: white;
        }

        .leaflet-control-zoom {
          border: none !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
        }

        .leaflet-control-zoom a {
          width: 36px !important;
          height: 36px !important;
          line-height: 36px !important;
          font-size: 18px !important;
          color: #374151 !important;
        }

        .leaflet-control-zoom a:hover {
          background: #f3f4f6 !important;
        }
      `}</style>
    </div>
  );
};

export default ZoneMapView;
